import { Description } from '@casavo/base-ui'
import { escapeRegExp } from 'lodash'
import useTranslation from 'next-translate/useTranslation'
import React, { useState } from 'react'

import { Translated } from '@/components/Translated'
import { useCities } from '@/context/Cities'
import { sprinkles } from '@/design-lib/style-theme/sprinkles.css'
import { FilterArea, FilterCity, FilterZone, GeoFilter } from '@/types/filter'
import provinceUtils from '@/utils/cities/province-utils'
import { GTMEventCategory } from '@/utils/tracking/eventCategory'
import { handleClick, handleFormFieldSet } from '@/utils/tracking/eventsHandlers'

import Item from './Item'
import { compareFilterZones } from './utils'

const isGeoFilterSelected = (filters: GeoFilter[], filter: GeoFilter): boolean =>
  filters.some((item) => item.slug === filter.slug)

type GeoFilterCategories =
  | GTMEventCategory.GeoFilterModalInCityPage
  | GTMEventCategory.BuyerInterestFlow
  | GTMEventCategory.MainSearchFlow

type Props = {
  activeFilters: GeoFilter[]
  eventCategory?: GeoFilterCategories
  hideProvince?: boolean
  listingCountByZone: FilterZone[]
  search: string
  setActiveFilters: (handler: (filter: GeoFilter[]) => GeoFilter[]) => void
  setSearch: (value: string) => void
}

const sortCities = (areaLabel: string, items: FilterCity[]): FilterCity[] =>
  [...items].sort((a, b) => {
    if (a.label === areaLabel) {
      return -1
    }
    if (b.label === areaLabel) {
      return 1
    }
    return 0
  })

const Body: React.FC<Props> = ({ eventCategory, ...props }) => {
  const cities = useCities()

  const shouldIncludeAreaFilter =
    !props.hideProvince && provinceUtils.citySupportsProvince(cities.router.area.name, cities.list)

  const { t } = useTranslation()

  const areaFilter: FilterArea = {
    kind: 'area',
    label: `${t('common:province-of', { area: cities.router.area.translatedCityName })}`,
    slug: cities.router.area.slug,
  }

  const cityFilters = sortCities(
    cities.router.area.name,
    cities.list.map(
      (city): FilterCity => ({
        kind: 'city',
        label: city.translatedCityName,
        slug: city.slug,
        zones: city.city_zones?.map((zone) => ({
          count: props.listingCountByZone
            ? props.listingCountByZone?.find((item) => item.slug === zone.slug)?.count || 0
            : undefined,
          kind: 'zone',
          label: zone.label,
          slug: zone.slug,
        })),
      })
    )
  )

  const items: GeoFilter[] = shouldIncludeAreaFilter ? [areaFilter, ...cityFilters] : cityFilters
  const [activeAccordions, setActiveAccordions] = useState(
    cityFilters
      .filter((item) => item.zones?.some((zone) => props.activeFilters.some((filter) => filter.slug === zone.slug)))
      .map((item) => item.label)
  )

  const handleFilterToggle = (
    filter: GeoFilter,
    accordionId?: string,
    doOtherFilterChanges: (f: GeoFilter[]) => GeoFilter[] = (f) => f
  ) => {
    if (!props.activeFilters.some((item) => item.slug === filter.slug) && accordionId) {
      setActiveAccordions((state) => (state.includes(accordionId) ? state : state.concat(accordionId)))
    }
    props.setActiveFilters((filters) => {
      if (!filters.some((item) => item.slug === filter.slug)) {
        return doOtherFilterChanges(filters.concat(filter))
      } else {
        return doOtherFilterChanges(filters.filter((item) => item.slug !== filter.slug))
      }
    })
  }

  const searchFilter = (item: { label?: string }) => item.label?.match(new RegExp(escapeRegExp(props.search), 'i'))
  const isAccordionOpen = (item: GeoFilter) =>
    activeAccordions.includes(item.label) ||
    (item.kind === 'city' && item.zones.some((zone) => searchFilter(zone) && props.search.length > 0))

  const handleClickOnCityWithZones = (item: FilterCity) => {
    eventCategory && handleFormFieldSet(eventCategory, 'CityZonesList', item.label)
    if (
      !props.activeFilters.some((filter) => filter.slug === item.slug) &&
      !item.zones.some((zone) => props.activeFilters.some((filter) => filter.slug === zone.slug)) &&
      !activeAccordions.includes(item.label)
    ) {
      props.setActiveFilters((activeFilters) => activeFilters.concat(item))
    }
    setActiveAccordions((state) =>
      state.includes(item.label) ? state.filter((accordion) => accordion !== item.label) : state.concat(item.label)
    )
  }

  const filteredItems = (item) =>
    item.kind === 'city' && item.zones?.length > 0 ? item.zones?.some(searchFilter) : searchFilter(item)

  return (
    <ul className={sprinkles({ flexGrow: 1, overflowY: 'auto' })}>
      {items.filter(filteredItems).length === 0 && (
        <Description className={sprinkles({ paddingY: 'xl', textAlign: 'center' })}>
          <Translated
            components={{
              '0': (
                <button
                  className={sprinkles({ cursor: 'pointer', textDecoration: 'underline' })}
                  onClick={() => {
                    props.setSearch('')
                    handleClick(eventCategory, 'ResetZones')
                  }}
                />
              ),
            }}
            i18Key="buyer-interest-flow:Zone_Noresult"
          />
        </Description>
      )}
      {items.filter(filteredItems).map((item) => {
        if (item.kind === 'area') {
          return (
            <Item
              key={item.label}
              isActive={props.activeFilters.length === 0}
              kind={item.kind}
              label={item.label}
              onClick={() => {
                eventCategory && handleFormFieldSet(eventCategory, 'AllArea', item.label)
                props.setActiveFilters(() => [])
              }}
            />
          )
        }

        if (item.kind === 'city' && item.zones?.length > 0) {
          // città con zone
          return (
            <Item
              key={item.label}
              isAccordionDropdownToggle
              activeChildrenCount={item.zones?.filter((zone) => isGeoFilterSelected(props.activeFilters, zone)).length}
              collapseControl={`${item.label}-zones-collapse`}
              dataTestId={`${item.label}-accordion-toggle`}
              isActive={
                item.zones.some((zone) => isGeoFilterSelected(props.activeFilters, zone)) ||
                isGeoFilterSelected(props.activeFilters, item)
              }
              isOpen={isAccordionOpen(item)}
              kind={item.kind}
              label={item.label}
              onClick={() => handleClickOnCityWithZones(item)}
            >
              {props.search.length === 0 && (
                <Item
                  count={props.listingCountByZone ? item.zones.reduce((acc, zone) => acc + zone.count, 0) : undefined}
                  dataTestId={`geofilter-modal-item-all-zones-${
                    isGeoFilterSelected(props.activeFilters, item) ? 'active' : 'default'
                  }`}
                  hasDivider={false}
                  isActive={isGeoFilterSelected(props.activeFilters, item)}
                  kind={item.kind}
                  label={t('zones:all-zones')}
                  onClick={() => {
                    eventCategory && handleFormFieldSet(eventCategory, 'AllCityZones', item.label)
                    handleFilterToggle(item, item.label, (activeFilters) =>
                      activeFilters.filter((filter) => !item.zones.some((zone) => zone.slug === filter.slug))
                    )
                  }}
                />
              )}

              {item.zones
                .filter(searchFilter)
                .sort(compareFilterZones)
                .map((zone) => {
                  return (
                    <Item
                      key={zone.label}
                      count={zone.count}
                      hasDivider={false}
                      isActive={isGeoFilterSelected(props.activeFilters, zone)}
                      kind={zone.kind}
                      label={zone.label}
                      onClick={() => {
                        if (zone.count === 0) {
                          return
                        }
                        eventCategory && handleFormFieldSet(eventCategory, 'Zone', `${item.label} - ${zone.label}`)
                        handleFilterToggle(zone, item.label, (activeFilters) => {
                          // remove city filter from active filters
                          return activeFilters.filter((filter) => item.slug !== filter.slug)
                        })
                      }}
                    />
                  )
                })}
            </Item>
          )
        }

        return (
          <Item
            key={item.label}
            isActive={isGeoFilterSelected(props.activeFilters, item)}
            kind={item.kind}
            label={item.label}
            onClick={() => {
              eventCategory && handleFormFieldSet(eventCategory, 'CityWithoutZones', item.label)
              handleFilterToggle(item)
            }}
          />
        )
      })}
    </ul>
  )
}

export default Body
